// utils/audioPlayback.js

// Throttling mechanism to prevent multiple rapid notifications
let lastPlayTime = 0;
const NOTIFICATION_THROTTLE_MS = 1000; // 1 second minimum between notifications
let currentAudio = null;

export function playNotificationSound(filePath) {
  try {
    // Create a new Audio object for each play to avoid conflicts
    const audio = new Audio();

    // Construct the correct path - remove 'assets/' prefix if it exists since we're already in the assets folder structure
    const cleanPath = filePath.startsWith("assets/")
      ? filePath.substring(7)
      : filePath;

    // Set the source to the correct path relative to the public folder
    audio.src = `/static/media/${cleanPath}`;

    // Alternative approach - try to import the file directly
    if (!audio.src || audio.src.includes("undefined")) {
      // For React apps, files in src/assets need to be imported
      audio.src =
        require(`../assets/${cleanPath}`).default ||
        require(`../assets/${cleanPath}`);
    }

    audio.currentTime = 0;

    // Add error handling
    audio.onerror = (e) => {
      console.error("Audio playback failed:", e);
      console.error("Attempted to play:", audio.src);
    };

    // Play the audio
    const playPromise = audio.play();

    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.error("Audio play failed:", error);
      });
    }
  } catch (error) {
    console.error("Error creating audio:", error);
    console.error("File path:", filePath);
  }
}

// Function to handle config object with isEnabled and audio properties
export function playNotificationFromConfig(config) {
  if (!config || !config.isEnabled || !config.audio) {
    console.log("Audio notification disabled or no audio file specified");
    return;
  }

  playNotificationSound(config.audio);
}

// Function with multiple fallback strategies for robust audio playback
export function playNotificationSoundRobust(filePath) {
  if (!filePath) {
    console.error("No file path provided for audio playback");
    return;
  }

  // Throttle notifications to prevent multiple rapid plays
  const now = Date.now();
  if (now - lastPlayTime < NOTIFICATION_THROTTLE_MS) {
    console.log("Notification throttled - too soon after last play");
    return;
  }
  lastPlayTime = now;

  // Stop any currently playing audio
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
  }

  const cleanPath = filePath.startsWith("assets/")
    ? filePath.substring(7)
    : filePath;

  // Strategy 1: Try direct path in public folder
  const tryPlayAudio = (src) => {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      audio.src = src;
      audio.currentTime = 0;

      audio.onloadeddata = () => {
        currentAudio = audio; // Track the current audio instance
        audio
          .play()
          .then(() => {
            // Clear reference when audio finishes
            audio.onended = () => {
              if (currentAudio === audio) currentAudio = null;
            };
            resolve(true);
          })
          .catch(reject);
      };

      audio.onerror = reject;

      // Timeout after 2 seconds
      setTimeout(() => reject(new Error("Timeout")), 2000);
    });
  };

  // Try different path strategies
  const pathStrategies = [
    `/assets/${cleanPath}`,
    `/${cleanPath}`,
    `/src/assets/${cleanPath}`,
    `/static/media/${cleanPath}`,
  ];

  // Try each strategy
  let currentStrategy = 0;

  const tryNext = () => {
    if (currentStrategy >= pathStrategies.length) {
      // Last resort: try to require the file
      try {
        const audio = new Audio();
        audio.src =
          require(`../assets/${cleanPath}`).default ||
          require(`../assets/${cleanPath}`);
        audio.currentTime = 0;
        currentAudio = audio;
        audio.onended = () => {
          if (currentAudio === audio) currentAudio = null;
        };
        audio.play().catch(console.error);
      } catch (requireError) {
        console.error("All audio playback strategies failed for:", filePath);
      }
      return;
    }

    const currentPath = pathStrategies[currentStrategy];

    tryPlayAudio(currentPath)
      .then(() => {
        console.log("Audio playback successful with strategy:", currentPath);
      })
      .catch(() => {
        currentStrategy++;
        tryNext();
      });
  };

  tryNext();
}
